import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '../ui';

interface TextWithTooltipProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  /** Called when input loses focus (used for touched state + any guards) */
  onBlur?: (value: string) => void;
  placeholder?: string;
  tooltipVisible?: boolean;
  tooltipContent?: React.ReactNode;
  fieldPath?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string | undefined;
}

const TextWithTooltip: React.FC<TextWithTooltipProps> = ({
  name,
  value = '',
  onChange,
  onBlur,
  placeholder = '',
  tooltipVisible = false,
  tooltipContent,
  fieldPath,
  disabled = false,
  error = false,
  errorMessage,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTooltip] = useState(tooltipVisible);
  const resolvedFieldPath = useMemo(() => fieldPath || name || undefined, [fieldPath, name]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Support global focus/highlight event used by red warning engine
  useEffect(() => {
    if (typeof window === 'undefined' || !resolvedFieldPath) return undefined;

    const highlightElement = (element: HTMLElement) => {
      const highlightRoot =
        (element.closest('[data-field-root="true"]') as HTMLElement | null) ?? element;

      highlightRoot.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      try {
        if (typeof (element as any).focus === 'function') {
          (element as HTMLInputElement).focus({ preventScroll: true });
        }
      } catch {
        // no-op
      }

      highlightRoot.classList.add('warning-highlight');
      window.setTimeout(() => {
        highlightRoot.classList.remove('warning-highlight');
      }, 2200);
    };

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ fieldPath?: string }>;
      if (!customEvent.detail || customEvent.detail.fieldPath !== resolvedFieldPath) return;
      const element = inputRef.current;
      if (!element) return;
      highlightElement(element);
    };

    window.addEventListener('iehub:focus-field', handler as EventListener);
    return () => {
      window.removeEventListener('iehub:focus-field', handler as EventListener);
    };
  }, [resolvedFieldPath]);

  // Handle input to only allow positive numbers (optional decimal)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty string
    if (inputValue === '') {
      onChange?.('');
      return;
    }

    // Normalize commas:
    // - If a dot is present, commas are treated as thousand separators and removed.
    // - If no dot is present and there is a single comma with up to 2 digits after it,
    //   treat it as a decimal separator (e.g. "12,5" => "12.5").
    // This keeps the control usable across different keyboards/locales while avoiding
    // mis-parsing thousand-separated values like "1,000".
    let normalized = inputValue;
    if (normalized.includes('.')) {
      normalized = normalized.replace(/,/g, '');
    } else {
      const commaCount = (normalized.match(/,/g) || []).length;
      if (commaCount === 1) {
        const parts = normalized.split(',');
        const right = parts[1] ?? '';
        if (right.length > 0 && right.length <= 2) {
          normalized = `${parts[0]}.${right}`;
        } else {
          normalized = normalized.replace(/,/g, '');
        }
      } else {
        normalized = normalized.replace(/,/g, '');
      }
    }

    // Remove any characters that aren't digits or decimal points
    let cleaned = normalized.replace(/[^\d.]/g, '');

    // Ensure only one decimal point
    const decimalCount = (cleaned.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = cleaned.indexOf('.');
      cleaned = cleaned.slice(0, firstDecimalIndex + 1) + cleaned.slice(firstDecimalIndex + 1).replace(/\./g, '');
    }

    // Limit integer part to 10 digits (warning shown at 6+ digits via UWBudgetField)
    const parts = cleaned.split('.');
    if (parts[0] && parts[0].length > 10) {
      parts[0] = parts[0].slice(0, 10);
      cleaned = parts.length === 2 ? `${parts[0]}.${parts[1]}` : parts[0];
    }

    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1] && parts[1].length > 2) {
      cleaned = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    if (onChange) {
      onChange(cleaned);
    }
  };

  return (
    <Box
      data-field-root={resolvedFieldPath ? 'true' : undefined}
      data-field-path={resolvedFieldPath}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          border: error ? `2px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 1,
          backgroundColor: disabled ? theme.palette.grey[50] : 'white',
          overflow: 'hidden',
          opacity: disabled ? 0.6 : 1,
          // Hover effect
          '&:hover': {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            borderWidth: '2px',
          },
          // Focus-within effect
          '&:focus-within': {
            borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
            borderWidth: '2px',
            boxShadow: error
              ? `0 0 0 2px ${theme.palette.mode === 'light' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(244, 67, 54, 0.2)'}`
              : `0 0 0 2px ${theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(144, 202, 249, 0.2)'}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: theme.palette.grey[50],
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          £
        </Box>
        <input
          type="text"
          inputMode="decimal"
          name={name}
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onBlur={(e) => onBlur?.(e.target.value)}
          placeholder={placeholder}
          data-field-path={resolvedFieldPath}
          disabled={disabled}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            padding: '12px 12px',
            fontSize: isMobile ? '16px' : '18px',
            lineHeight: isMobile ? '22px' : '24px',
            backgroundColor: 'transparent',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      </Box>

      {/* Error message below input */}
      {(error || errorMessage) && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mt: 1,
            fontSize: '0.875rem',
            fontWeight: 500
          }}
        >
          {errorMessage || 'This field is required'}
        </Typography>
      )}

      {showTooltip && tooltipContent && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: theme.palette.mode === 'light' ? '#E8F4F3' : theme.palette.grey[800],
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1,
          }}
        >
          {tooltipContent}
        </Box>
      )}
    </Box>
  );
};

export default TextWithTooltip;
